// feature-module/student/pages/tabs/StudentAbsencesTab.tsx
import React, { useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { updateAbsence } from "services/absenceService";
import useStudent from "../hooks/useStudent";
import { message, Space } from "antd";
import Loader from "components/Common/loader";
import ActionsDropdown, { Action } from "components/Common/ActionsDropdown";
import { Absence } from "types/absenceTypes";
import Badge from "components/Common/ui/Badge";
import { formatDateTime, formatTimeDuration } from "utils/dateUtils";
import UserProfileHeader from "components/Common/ui/UserProfileHeader";
import Datatable from "components/Common/dataTable";
import { getSubjectLabelFromCode } from "utils/schoolUtils";
import { useAbility } from "config/abilityContext";

type Ctx = { student: any | null };

const StudentAbsencesTab: React.FC = () => {
    const { student } = useOutletContext<Ctx>();
    const { t } = useTranslation();
    const ability = useAbility();

    const {
        fetchAbsencesByStudentId,
        absences,
        loading
    } = useStudent();

    const refresh = useCallback(() => {
        if (!student?.id) return;
        fetchAbsencesByStudentId(student.id);
    }, [student?.id]);

    useEffect(() => {
        if (student?.id) refresh();
    }, [student?.id]);


    const handleUpdateStatus = async (absenceId: string, status: "excused" | "unexcused") => {
        try {
            await updateAbsence(absenceId, { status });
            refresh();
            message.success(
                status === "excused" ? t("absence_marked_as_excused") : t("absence_marked_as_unexcused")
            );
        } catch {
            message.error(t("failed_to_update_absence"));
        }
    };

    const columns = [
        {
            title: t("teacher"),
            dataIndex: "teacher",
            key: "teacher",
            render: (_: any, record: Absence) => (
                <UserProfileHeader
                    photo={record.teacher?.user?.photo}
                    firstName={record.teacher?.user?.firstName || ""}
                    lastName={record.teacher?.user?.lastName || ""}
                />
            )
        },
        {
            title: t("subject"),
            dataIndex: "subject",
            key: "subject",
            render: (_: any, record: Absence) => getSubjectLabelFromCode(record.subject?.code || "") || "-",
        },
        {
            title: t("date"),
            dataIndex: "dateTime",
            key: "dateTime",
            render: (dateTime: string) => formatDateTime(dateTime),
        },
        {
            title: t("duration"),
            dataIndex: "duration",
            key: "duration",
            render: (_: any, record: Absence) => formatTimeDuration(record.duration ?? 0),
        },
        {
            title: t("status"),
            dataIndex: "status",
            key: "status",
            render: (_: any, record: Absence) => {
                const hasProof = Boolean(record.proofUrl && record.proofUrl.trim() !== '');
                const status = record.status || 'pending';

                return (
                    <Space>
                        <Badge
                            variant={status === "excused" ? "success" : status === "unexcused" ? "danger" : "warning"}
                            text={status}
                        />
                        {hasProof && (
                            <i
                                className="ti ti-file"
                                style={{ color: "green", fontSize: "16px", cursor: "pointer" }}
                                onClick={() => window.open(record.proofUrl, '_blank')}
                                title={t("download_proof")}
                            />
                        )}
                    </Space>
                );
            },
        },
        {
            title: t("action"),
            key: "action",
            hidden: !ability.can("update", "Absence"),
            render: (_: any, record: Absence) => {
                const status = record.status || "pending";
                const actions: Action[] = [];
                if (record.status === "pending") {
                    actions.push({
                        label: t("mark_excused"),
                        actionType: "modal",
                        onClick: () => handleUpdateStatus(record.id!, "excused"),
                        icon: "ti ti-check",
                    });
                    actions.push({
                        label: t("mark_unexcused"),
                        actionType: "modal",
                        onClick: () => handleUpdateStatus(record.id!, "unexcused"),
                        icon: "ti ti-x",
                    });
                }
                else if (record.status === "unexcused") {
                    actions.push({
                        label: t("mark_excused"),
                        actionType: "modal",
                        onClick: () => handleUpdateStatus(record.id!, "excused"),
                        icon: "ti ti-check",
                    });
                }
                return <ActionsDropdown actions={actions} />;
            }
        },
    ];


    if (!student) return null;
    if (loading) return <Loader />;
    return (
        <div className="p-2">
            <Datatable columns={columns} dataSource={absences ?? []} pagination={true} />
        </div>
    );
};

export default StudentAbsencesTab;
